import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  FaServer, FaShieldAlt, FaNetworkWired, FaTv, FaPrint, FaTools, 
  FaBuilding, FaHandshake, FaRocket, FaAward, FaTrophy, 
  FaHeart, FaCheckCircle, FaChevronDown, FaLock, FaCog, FaLightbulb, FaQuestionCircle
} from 'react-icons/fa';
import { useScrollAnimation } from '../../hooks';
import { useLanguage } from '../../contexts/LanguageContext';

const About = () => {
  const { t, lang } = useLanguage();
  const location = useLocation();
  
  const [setBannerRef, bannerInView] = useScrollAnimation(0.1);
  const [setTimelineRef, timelineInView] = useScrollAnimation(0.1);
  const [setSolutionsRef, solutionsInView] = useScrollAnimation(0.1);
  const [setValuesRef, valuesInView] = useScrollAnimation(0.1);
  const [setFaqRef, faqInView] = useScrollAnimation(0.1);

  const [expandedFaq, setExpandedFaq] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const bannerImages = ["/images/server1.jpg", "/images/server2.jpg", "/images/server3.jpg", "/images/server5.avif"];

  // S'assurer que le scroll se fait au chargement de la page si on arrive depuis l'accueil avec une ancre (#)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, [location]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % bannerImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  const timelineIcons = [<FaBuilding />, <FaHandshake />, <FaShieldAlt />, <FaRocket />, <FaAward />, <FaTrophy />];
  const expertiseIcons = [<FaNetworkWired />, <FaRocket />, <FaShieldAlt />, <FaTools />];
  const faqIcons = [<FaCog />, <FaNetworkWired />, <FaLock />, <FaShieldAlt />, <FaAward />, <FaTrophy />, <FaLightbulb />, <FaHandshake />];
  
  const pillarImages = ["/images/innovation.jpeg", "/images/engagement.jpg", "/images/excellence.jpg"];

  const solutionsData = [
    { icon: <FaServer />, title: lang === 'fr' ? "Infrastructure Data Center" : "Data Center Infrastructure", image: "/images/datacenter.webp", items: lang === 'fr' ? ["Serveurs", "Stockage", "HCI", "Backup", "Cloud"] : ["Servers", "Storage", "HCI", "Backup", "Cloud"] },
    { icon: <FaShieldAlt />, title: lang === 'fr' ? "Cyber Sécurité" : "Cyber Security", image: "/images/security.webp", items: lang === 'fr' ? ["Firewall", "WAF", "Load Balancer", "Anti-virus"] : ["Firewall", "WAF", "Load Balancer", "Anti-virus"] },
    { icon: <FaNetworkWired />, title: lang === 'fr' ? "Solutions Réseau" : "Network Solutions", image: "/images/network.avif", items: ["Switching", "WIFI", "Access Points"] },
    { icon: <FaTv />, title: lang === 'fr' ? "Affichage Dynamique" : "Digital Signage", image: "/images/display.jpg", items: lang === 'fr' ? ["Mur d'écrans", "Digital Signage"] : ["Video Wall", "Digital Signage"] },
    { icon: <FaPrint />, title: lang === 'fr' ? "Solutions Printing" : "Printing Solutions", image: "/images/printing.avif", items: lang === 'fr' ? ["Gestion d'impression", "Management"] : ["Print Management", "Management"] },
    { icon: <FaTools />, title: lang === 'fr' ? "Services Support" : "Support Services", image: "/images/support.jpg", items: lang === 'fr' ? ["Maintenance", "Support 24/7", "Assistance"] : ["Maintenance", "24/7 Support", "Assistance"] }
  ];

  const toggleFaq = (index) => setExpandedFaq(prev => prev === index ? null : index);

  return (
    <div className="about-page">
      {/* ── BANNER ── */}
      <section ref={setBannerRef} style={{ position: 'relative', height: '70vh', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {bannerImages.map((image, index) => (
            <div key={index} style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.4)), url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: index === currentImageIndex ? 1 : 0, transition: 'opacity 1s ease-in-out' }} />
          ))}
        </div>
        <Container style={{ position: 'relative', zIndex: 2 }}>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <motion.div initial={{ opacity: 0, y: 50 }} animate={bannerInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <h1 style={{ color: '#fff', fontSize: '3.5rem', fontWeight: 800, marginBottom: '1.5rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }} dangerouslySetInnerHTML={{ __html: t.about.banner.title }}></h1>
                <p style={{ color: 'rgba(255,255,255,0.95)', fontSize: '1.4rem', fontWeight: 500, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>{t.about.banner.subtitle}</p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── TIMELINE (ID: history) ── */}
      <section id="history" ref={setTimelineRef} style={{ padding: '5rem 0', background: '#f8f9fa' }}>
        <Container>
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#333' }}>{t.about.timeline.title}</h2>
            <p style={{ fontSize: '1.2rem', color: '#666' }}>{t.about.timeline.sub}</p>
          </div>
          <Row className="justify-content-center">
            <Col lg={10} style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '4px', background: 'linear-gradient(to bottom, #d90000, #ff6b6b)', transform: 'translateX(-50%)', zIndex: 1 }} />
              {t.about.timeline.events.map((event, index) => (
                <motion.div key={index} initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }} animate={timelineInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.2 }} style={{ display: 'flex', alignItems: 'center', marginBottom: '3rem', position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '60px', background: '#d90000', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.5rem', zIndex: 2, boxShadow: '0 4px 16px rgba(217,0,0,0.3)' }}>{timelineIcons[index]}</div>
                  <div style={{ width: '45%', marginLeft: index % 2 === 0 ? 0 : 'auto', textAlign: index % 2 === 0 ? 'right' : 'left' }}>
                    <Card style={{ padding: '1.5rem', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: '12px' }}>
                      <h3 style={{ color: '#d90000', fontSize: '1.8rem', fontWeight: 700 }}>{event.year}</h3>
                      <h4 style={{ fontWeight: 600 }}>{event.title}</h4>
                      <p style={{ color: '#666', margin: 0 }}>{event.desc}</p>
                    </Card>
                  </div>
                </motion.div>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── EXPERTISE ── */}
      <section style={{ padding: '8rem 0', background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(71, 85, 105, 0.95)), url('/images/datacenter2.jpg')`, backgroundSize: 'cover', backgroundAttachment: 'fixed', position: 'relative' }}>
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.8rem', fontWeight: 700, color: '#ffffff', marginBottom: '1.5rem' }} dangerouslySetInnerHTML={{ __html: t.about.expertise.title }}></h2>
            <p style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', maxWidth: '700px', margin: '0 auto' }}>{t.about.expertise.sub}</p>
          </div>
          <Row className="g-4">
            {t.about.expertise.cards.map((card, i) => (
              <Col lg={6} key={i}>
                <motion.div whileHover={{ y: -12, scale: 1.03 }} style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', height: '100%', borderTop: '3px solid #dc2626' }}>
                  <div style={{ background: 'rgba(220,38,38,0.1)', borderRadius: '16px', width: '70px', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2rem' }}>
                    <span style={{ fontSize: '2rem', color: '#dc2626' }}>{expertiseIcons[i]}</span>
                  </div>
                  <h3 style={{ color: '#1a202c', fontWeight: 700, marginBottom: '1.25rem', fontSize: '1.3rem' }}>{card.title}</h3>
                  <p style={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: '1.6' }}>{card.desc}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── SOLUTIONS ── */}
      <section ref={setSolutionsRef} style={{ padding: '5rem 0' }}>
        <Container>
          <h2 className="text-center mb-5" style={{ fontSize: '2.5rem', fontWeight: 700 }}>{t.about.solutionsTitle}</h2>
          <Row className="g-4">
            {solutionsData.map((sol, index) => (
              <Col lg={4} md={6} key={index}>
                <motion.div whileHover={{ y: -10 }}>
                  <Card style={{ height: '100%', border: 'none', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                    <div style={{ height: '200px', backgroundImage: `url(${sol.image})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '3rem' }}>{sol.icon}</div>
                    </div>
                    <Card.Body style={{ padding: '1.5rem' }}>
                      <h4 style={{ color: '#333', fontWeight: 700 }}>{sol.title}</h4>
                      <ul className="list-unstyled">
                        {sol.items.map((it, idx) => (
                          <li key={idx} className="mb-2 d-flex align-items-center"><FaCheckCircle style={{ color: '#d90000', marginRight: '0.5rem', fontSize: '0.9rem' }} />{it}</li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── VALUES (ID: values) ── */}
      <section id="values" ref={setValuesRef} style={{ padding: '5rem 0', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <Container>
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: 700 }}>{t.about.values.title}</h2>
            <p style={{ fontSize: '1.2rem', color: '#666' }}>{t.about.values.sub}</p>
          </div>
          <Row className="g-4 justify-content-center">
            {t.about.values.items.map((val, i) => (
              <Col lg={4} key={i}>
                <div className="value-card">
                  <div className="value-bg-image" style={{ backgroundImage: `url(${pillarImages[i]})` }} />
                  <div className="value-overlay" />
                  <div className="value-content">
                    <h3 className="value-title">{val.title}</h3>
                    <p className="value-description">{val.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>

        <style>{`
          .value-card { position: relative; height: 400px; border-radius: 20px; overflow: hidden; cursor: pointer; box-shadow: 0 8px 32px rgba(0,0,0,0.1); background: #000; }
          .value-bg-image { position: absolute; inset: 0; background-size: cover; background-position: center; transition: transform 0.6s ease, filter 0.6s ease; }
          .value-overlay { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%); transition: opacity 0.4s ease; }
          .value-content { position: relative; z-index: 2; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 2rem; text-align: center; }
          .value-title { color: #fff; font-weight: 700; font-size: 1.8rem; margin: 0; transition: transform 0.4s ease; text-shadow: 2px 2px 8px rgba(0,0,0,0.5); }
          .value-description { color: rgba(255,255,255,0.9); font-size: 1.1rem; line-height: 1.5; margin-top: 1.5rem; opacity: 0; transform: translateY(30px); transition: opacity 0.4s ease, transform 0.4s ease; max-width: 90%; }
          .value-card:hover .value-bg-image { transform: scale(1.1); filter: blur(4px) brightness(0.6); }
          .value-card:hover .value-overlay { background: rgba(0,0,0,0.8); }
          .value-card:hover .value-title { transform: translateY(-20px); }
          .value-card:hover .value-description { opacity: 1; transform: translateY(0); }
        `}</style>
      </section>

      {/* ── FAQ (ID: faq) ── */}
      <section id="faq" ref={setFaqRef} style={{ padding: '5rem 0', background: '#fff' }}>
        <Container>
          <div className="text-center mb-5">
            <h2 style={{ fontSize: '2.5rem', fontWeight: 700 }}>{t.about.faq.title}</h2>
            <p style={{ fontSize: '1.2rem', color: '#666' }}>{t.about.faq.sub}</p>
          </div>
          <Row className="justify-content-center">
            <Col lg={10}>
              {t.about.faq.data.map((faq, index) => (
                <div key={index} style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <div onClick={() => toggleFaq(index)} style={{ padding: '1.5rem 2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: expandedFaq === index ? '#f8f9fa' : '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <div style={{ background: 'linear-gradient(135deg, #d90000, #ff4444)', color: 'white', width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem', fontSize: '1.2rem' }}>{faqIcons[index] || <FaQuestionCircle />}</div>
                      <h5 style={{ fontWeight: 600, color: '#333', margin: 0, fontSize: '1.1rem', textAlign: 'left' }}>{faq.q}</h5>
                    </div>
                    <FaChevronDown style={{ color: '#d90000', transition: '0.3s', transform: expandedFaq === index ? 'rotate(180deg)' : 'none' }} />
                  </div>
                  <AnimatePresence>
                    {expandedFaq === index && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4 }}>
                        <div style={{ padding: '2rem', paddingTop: '1.5rem', color: '#666', lineHeight: '1.8', textAlign: 'justify', whiteSpace: 'pre-wrap', borderTop: '1px solid #e0e0e0' }}>
                          {faq.a.split('**').map((text, idx) => idx % 2 === 1 ? <strong key={idx} style={{ color: '#d90000' }}>{text}</strong> : text)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default About;